import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useAllNetworks } from "../Networks/networkUtils";
import { compactFieldSx } from "./formStyles";

export type NetworkOption = {
  id: string;
  name: string;
  sponsorId: string | null;
  sponsorName?: string | null;
};

type Props = {
  selectedNetworkId?: string;
  onSelect: (network: NetworkOption | null) => void;
};

// Pick-only network selector. Uses the existing Redux-backed `useAllNetworks`
// hook — networks are loaded by the auth flow for SysAdmins, so usually no
// API call here. Falls back to triggering the load if Redux is empty.
export function NetworkSelector({ selectedNetworkId, onSelect }: Props) {
  const { networks, status, error, getAllNetworks } = useAllNetworks();

  useEffect(() => {
    if (status === "idle" && (!networks || networks.length === 0)) {
      void getAllNetworks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const options = useMemo<NetworkOption[]>(() => {
    const opts: NetworkOption[] = (networks ?? [])
      .filter((n): n is NonNullable<typeof n> => !!n)
      .map((n) => ({
        id: n.id,
        name: n.name,
        sponsorId: n.sponsorId ?? null,
        sponsorName: n.sponsor?.name ?? null
      }));
    opts.sort((a, b) => a.name.localeCompare(b.name));
    return opts;
  }, [networks]);

  const loading = status === "loading";
  const selected = options.find((n) => n.id === selectedNetworkId) ?? null;

  return (
    <Box>
      <Autocomplete
        size="small"
        loading={loading}
        options={options}
        value={selected}
        getOptionLabel={(opt) => opt.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, val) => onSelect(val)}
        sx={{ ...compactFieldSx, minWidth: 320, maxWidth: 480 }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Target network"
            placeholder={loading ? "Loading networks…" : "Select a network"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, opt) => (
          <Box component="li" {...props} key={opt.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {opt.name}
              </Typography>
              {opt.sponsorName ? (
                <Typography variant="caption" color="text.secondary">
                  Sponsor: {opt.sponsorName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
      />
      {error ? (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
    </Box>
  );
}
